import { useEffect, useState } from 'react';
import { supportsFileSystemAccess } from '@/storage/fileIO';
import type { StorageUsage } from '@/storage/StorageProvider';
import { getServices } from '@/state/services';
import { formatBytes, useDaw } from '@/state/store';

/**
 * The status bar is where the DAW tells the truth about itself: whether audio
 * is running, where the project lives, how much room is left, and whether the
 * browser has promised not to evict it. A local-first app has to make its
 * storage situation legible, because the user's work depends on it.
 */
export function StatusBar(): JSX.Element {
  const project = useDaw((s) => s.project);
  const audioReady = useDaw((s) => s.audioReady);
  const dirty = useDaw((s) => s.dirty);
  const lastAutosaveAt = useDaw((s) => s.lastAutosaveAt);
  const transportState = useDaw((s) => s.transportState);
  const stretchProgress = useDaw((s) => s.stretchProgress);

  const [usage, setUsage] = useState<StorageUsage>({
    usageBytes: null,
    quotaBytes: null,
    persisted: false,
    renderBytes: 0,
    renderBudgetBytes: 0,
  });
  const [online, setOnline] = useState(() => navigator.onLine);
  const pluginLatencySec = useDaw((s) => s.pluginLatencySec);
  const [engine, setEngine] = useState({ sampleRate: 0, latency: 0, voices: 0 });
  const [loudness, setLoudness] = useState<{ momentary: number; integrated: number } | null>(null);

  useEffect(() => {
    const services = getServices();
    const update = (): void => {
      void services.storage.estimateUsage().then(setUsage);
      const status = services.engine.status();
      setEngine({
        sampleRate: status.sampleRate,
        latency: (status.baseLatencySec + status.outputLatencySec) * 1000,
        voices: status.voiceCount,
      });
    };
    update();
    const id = setInterval(update, 4000);
    return () => clearInterval(id);
  }, [audioReady, project.meta.id]);

  // The loudness readout updates several times a second while audio is playing
  // and stops entirely when it is not: a number that moves when nothing is
  // happening is a number nobody trusts.
  useEffect(() => {
    if (transportState === 'stopped') {
      setLoudness(null);
      return;
    }
    const services = getServices();
    const id = setInterval(() => {
      const reading = services.engine.readLoudness();
      setLoudness(
        reading && Number.isFinite(reading.momentaryLufs)
          ? { momentary: reading.momentaryLufs, integrated: reading.integratedLufs }
          : null,
      );
    }, 250);
    return () => clearInterval(id);
  }, [transportState]);

  useEffect(() => {
    const on = (): void => setOnline(true);
    const off = (): void => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);

  const stretching = useDaw((s) => s.stretching);
  const clipCount = Object.keys(project.clips).length;
  const assetCount = Object.keys(project.assets).length;

  return (
    <footer className="statusbar">
      <span className="statusbar__item">
        <span className={`statusbar__dot ${audioReady ? 'statusbar__dot--good' : 'statusbar__dot--warn'}`} />
        {audioReady
          ? `Audio ${(engine.sampleRate / 1000).toFixed(1)} kHz · ${engine.latency.toFixed(0)} ms`
          : 'Audio idle — press play to start'}
      </span>

      {pluginLatencySec > 0.0005 && (
        <span
          className="statusbar__item"
          title="A plugin answers late, and every route is held back to it so everything arrives together. This is how much later than written the whole mix now sounds — and what you feel between a key and its note."
        >
          <span className="statusbar__dot statusbar__dot--warn" />
          {`Plugins +${(pluginLatencySec * 1000).toFixed(0)} ms`}
        </span>
      )}

      {loudness && (
        <span className="statusbar__item" title="Momentary loudness now, and the integrated figure since playback started.">
          <span className="statusbar__dot statusbar__dot--good" />
          {loudness.momentary.toFixed(1)} LUFS
          {Number.isFinite(loudness.integrated) ? ` · int ${loudness.integrated.toFixed(1)}` : ''}
        </span>
      )}

      {stretching && (
        <span
          className="statusbar__item"
          title="Stretched, transposed and bent clips are rendered before they play, in the order the playhead reaches them."
        >
          <span className="statusbar__dot statusbar__dot--warn" />
          {stretchProgress && stretchProgress.total > 1
            ? `Preparing audio… ${stretchProgress.done + 1} of ${stretchProgress.total}`
            : 'Preparing audio…'}
        </span>
      )}

      {useDaw.getState().midiStatus === 'open' && (
        <span className="statusbar__item">
          <span className="statusbar__dot statusbar__dot--good" />
          MIDI in
        </span>
      )}

      {transportState === 'recording' && (
        <span className="statusbar__item" style={{ color: 'var(--record)' }}>
          <span className="statusbar__dot" style={{ background: 'var(--record)' }} />
          Recording — stored on this computer only
        </span>
      )}

      <span className="statusbar__item">
        {project.trackOrder.length} tracks · {clipCount} clips · {assetCount} audio files
      </span>

      <span className="statusbar__spacer" />

      <span
        className="statusbar__item"
        title={
          'Stage Zero stores everything in this browser, on this device.' +
          (usage.renderBudgetBytes > 0
            ? ` Stretched, reversed and transposed audio is kept between sessions: ${formatBytes(
                usage.renderBytes,
              )} of ${formatBytes(usage.renderBudgetBytes)}. It is rebuilt if dropped.`
            : '')
        }
      >
        {usage.usageBytes !== null
          ? `Local storage ${formatBytes(usage.usageBytes)}${usage.quotaBytes ? ` of ${formatBytes(usage.quotaBytes)}` : ''}`
          : 'Local storage'}
        {usage.persisted ? ' · protected' : ''}
      </span>

      <span className="statusbar__item">
        {dirty ? 'Unsaved changes' : 'Saved'}
        {lastAutosaveAt ? ` · recovery point ${new Date(lastAutosaveAt).toLocaleTimeString()}` : ''}
      </span>

      <span className="statusbar__item" title="Stage Zero needs no connection once it has loaded.">
        {online ? 'Online (not required)' : 'Offline — everything still works'}
      </span>

      {!supportsFileSystemAccess() && (
        <span className="statusbar__item" title="Exports will download instead of writing back to a chosen file.">
          Downloads mode
        </span>
      )}
    </footer>
  );
}
